import React, { useState, useEffect } from 'react'
import { useNavigate, useLocation, useSearchParams } from 'react-router-dom'
import { useAuth } from '../../shared/auth/store'
import { http } from '../../shared/api/http'
import { RegistrationForm, PasswordResetRequestForm, PasswordResetConfirmForm } from '../../shared/components/auth'
import { logger } from '../../shared/utils/logger'

// Компонент анимированного фона (копия из App.tsx)
const AnimatedBackground: React.FC<{ children: React.ReactNode }> = ({ children }) => (
  <div style={{
    position: 'fixed',
    top: 0,
    left: 0,
    right: 0,
    bottom: 0,
    background: 'linear-gradient(135deg, #667eea 0%, #764ba2 50%, #f093fb 100%)',
    display: 'flex',
    justifyContent: 'center',
    alignItems: 'flex-start',
    flexDirection: 'column',
    paddingTop: '1rem',
    padding: '0.5rem',
    color: 'white',
    fontFamily: 'system-ui, -apple-system, sans-serif'
  }}>
    {/* Анимированные частицы на фоне */}
    <div style={{
      position: 'absolute',
      top: 0,
      left: 0,
      right: 0,
      bottom: 0,
      overflow: 'hidden',
      pointerEvents: 'none'
    }}>
      {[...Array(20)].map((_, i) => (
        <div
          key={i}
          style={{
            position: 'absolute',
            width: Math.random() * 4 + 2 + 'px',
            height: Math.random() * 4 + 2 + 'px',
            background: 'rgba(255, 255, 255, 0.3)',
            borderRadius: '50%',
            left: Math.random() * 100 + '%',
            top: Math.random() * 100 + '%',
            animation: `float ${Math.random() * 3 + 2}s ease-in-out infinite alternate`
          }}
        />
      ))}
    </div>

    {/* Контент */}
    <div style={{ zIndex: 1, width: '100%', height: '100%', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
      {children}
    </div>

    {/* CSS анимации */}
    <style>{`
      @keyframes float {
        0% { transform: translateY(0px); }
        100% { transform: translateY(-20px); }
      }
    `}</style>
  </div>
)

interface LoginFormData {
  email: string
  password: string
}



export const LoginPage: React.FC = () => {
  const navigate = useNavigate()
  const location = useLocation()
  const [searchParams] = useSearchParams()
  const { isAuthenticated, setAuth, setError, error } = useAuth()
  
  // Функция для определения, нужно ли скрывать ошибку регистрации в MiniApp
  const shouldHideRegistrationError = (errorMessage: string): boolean => {
    const isMiniApp = location.pathname.startsWith('/miniapp')
    const isRegistrationError = errorMessage.includes('не зарегистрирован') || 
                               errorMessage.includes('not registered') ||
                               errorMessage.includes('Пользователь не зарегистрирован')
    return isMiniApp && isRegistrationError
  }
  
  const [mode, setMode] = useState<'login' | 'register' | 'password_reset' | 'password_reset_confirm'>('login')
  const [isLoading, setIsLoading] = useState(false)
  const [successMessage, setSuccessMessage] = useState('')
  
  const [loginData, setLoginData] = useState<LoginFormData>({
    email: '',
    password: ''
  })
  
  const [fieldErrors, setFieldErrors] = useState<Record<string, string>>({})
  
  // Проверяем параметры URL
  const resetToken = searchParams.get('token')
  const urlMode = searchParams.get('mode')
  
  useEffect(() => {
    if (resetToken && mode !== 'password_reset_confirm') {
      setMode('password_reset_confirm')
    } else if (urlMode === 'password_reset' && mode !== 'password_reset') {
      setMode('password_reset')
    }
  }, [resetToken, urlMode, mode])

  // Перенаправляем авторизованных пользователей (кроме восстановления пароля)
  useEffect(() => {
    // Если есть токен восстановления в URL, не перенаправляем
    if (resetToken) {
      return
    }
    
    if (isAuthenticated && mode !== 'password_reset_confirm' && mode !== 'password_reset') {
      // После logout всегда перенаправляем на главную страницу
      navigate('/orders', { replace: true })
    }
  }, [isAuthenticated, navigate, mode, resetToken])

  const validateEmail = (email: string): boolean => {
    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/
    return emailRegex.test(email)
  }

  const handleLogin = async (e: React.FormEvent) => {
    e.preventDefault()
    
    const errors: Record<string, string> = {}
    
    if (!validateEmail(loginData.email)) {
      errors.email = 'Введите корректный email'
    }
    
    if (!loginData.password || loginData.password.length < 6) {
      errors.password = 'Пароль должен содержать минимум 6 символов'
    }
    
    setFieldErrors(errors)
    if (Object.keys(errors).length > 0) return
    
    setIsLoading(true)
    setError(null)
    setSuccessMessage('')
    
    try {
      // ИСПРАВЛЕННАЯ логика определения миниапп режима
      let telegramId: string | undefined
      let isTelegramMode = false
      
      // 1) ПРИОРИТЕТ: Определяем Telegram режим по наличию WebApp API или пути
      const pathname = window.location.pathname
      if (pathname.startsWith('/miniapp')) {
        isTelegramMode = true
        logger.log('📱 Telegram режим определен по пути /miniapp')
      }
      
      // 1.1) ВАЖНО: Проверяем наличие Telegram WebApp API (даже если путь не /miniapp)
      if (!isTelegramMode && typeof window !== 'undefined') {
        // Проверяем несколько раз с задержкой, так как Telegram WebApp API может загружаться асинхронно
        for (let attempt = 0; attempt < 5; attempt++) {
          const webApp = (window as any)?.Telegram?.WebApp
          if (webApp) {
            isTelegramMode = true
            logger.log(`📱 Telegram режим определен по наличию WebApp API (попытка ${attempt + 1})`)
            break
          }
          
          // Дополнительные проверки для Telegram окружения
          const hasTelegramTheme = document.documentElement.hasAttribute('data-telegram-theme')
          const hasTelegramVars = getComputedStyle(document.documentElement).getPropertyValue('--tg-theme-bg-color')
          
          if (hasTelegramTheme || hasTelegramVars) {
            isTelegramMode = true
            logger.log('📱 Telegram режим определен по CSS переменным Telegram')
            break
          }
          
          // Ждем перед следующей попыткой
          if (attempt < 4) {
            await new Promise(resolve => setTimeout(resolve, 200))
          }
        }
      }
      
      // 2) Дополнительная проверка Telegram окружения (только если не определен по пути)
      if (!isTelegramMode) {
        const urlParams = new URLSearchParams(window.location.search)
        const userAgent = navigator.userAgent.toLowerCase()
        const hash = window.location.hash
        
        const webApp = (window as any)?.Telegram?.WebApp
        const hasWebAppObject = !!webApp
        const hasRealWebAppData = !!(webApp && (webApp.initDataUnsafe?.user?.id || (webApp.initData && webApp.initData.length > 0)))
        const hasTelegramUrlHints = !!(urlParams.get('tgWebAppPlatform') || urlParams.get('tgWebAppVersion') || hash.includes('tgWebAppData='))
        const isTelegramUA = userAgent.includes('telegram')
        
        if (hasWebAppObject || hasRealWebAppData || hasTelegramUrlHints || isTelegramUA) {
          isTelegramMode = true
          logger.log('📱 Telegram режим определен по окружению:', { hasWebAppObject, hasRealWebAppData, hasTelegramUrlHints, isTelegramUA })
        }
      }
      
      // 3) Если это Telegram режим, получаем telegram_id разными способами
      if (isTelegramMode) {
        // Метод 1: Прямая проверка WebApp API (с повторными попытками)
        for (let attempt = 0; attempt < 3; attempt++) {
          const webApp = (window as any)?.Telegram?.WebApp
          if (webApp?.initDataUnsafe?.user?.id) {
            telegramId = webApp.initDataUnsafe.user.id.toString()
            logger.log(`✅ telegram_id получен из WebApp API (попытка ${attempt + 1}):`, telegramId)
            break
          }
          
          if (attempt < 2) {
            await new Promise(resolve => setTimeout(resolve, 300))
          }
        }
        
        // Метод 2: Парсинг initData напрямую (если WebApp API недоступен)
        if (!telegramId) {
          const webApp = (window as any)?.Telegram?.WebApp
          if (webApp?.initData) {
            try {
              const initData = webApp.initData
            const parsed = new URLSearchParams(initData)
            const userStr = parsed.get('user')
            if (userStr) {
              const user = JSON.parse(decodeURIComponent(userStr))
              if (user.id) {
                telegramId = user.id.toString()
                logger.log('✅ telegram_id получен из initData:', telegramId)
              }
            }
            } catch (error) {
              logger.log('⚠️ Ошибка парсинга initData:', error)
            }
          }
        }
        
        // Метод 3: Из localStorage (сохраненный ранее)
        if (!telegramId) {
          telegramId = localStorage.getItem('telegram_id') || undefined
          if (telegramId) {
            logger.log('✅ telegram_id получен из localStorage:', telegramId)
          }
        }
        
        // Метод 3.1: Попытка получить из сохраненного пользователя
        if (!telegramId) {
          try {
            const savedUser = localStorage.getItem('alfastil_user')
            if (savedUser) {
              const userData = JSON.parse(savedUser)
              if (userData.id && !userData.client_id) {
                telegramId = userData.id.toString()
                logger.log('✅ telegram_id получен из сохраненного пользователя:', telegramId)
              }
            }
          } catch (error) {
            logger.log('⚠️ Ошибка парсинга сохраненного пользователя:', error)
          }
        }
        
        // Метод 4: Из authStore (если пользователь уже авторизован)
        if (!telegramId) {
          try {
            const { useAuthStore } = await import('../../shared/auth/store')
            const authStore = useAuthStore.getState()
            if (authStore.user?.id) {
              telegramId = authStore.user.id.toString()
              logger.log('✅ telegram_id получен из authStore:', telegramId)
            }
          } catch (error) {
            logger.log('⚠️ Ошибка получения authStore:', error)
          }
        }
        
        // Метод 5: Попытка получить из URL параметров (fallback)
        if (!telegramId) {
          const urlParams = new URLSearchParams(window.location.search)
          const hash = window.location.hash
          
          // Проверяем URL параметры
          const tgWebAppData = urlParams.get('tgWebAppData') || hash.match(/tgWebAppData=([^&]+)/)?.[1]
          if (tgWebAppData) {
            try {
              const decodedData = decodeURIComponent(tgWebAppData)
              const parsed = new URLSearchParams(decodedData)
              const userStr = parsed.get('user')
              if (userStr) {
                const user = JSON.parse(decodeURIComponent(userStr))
                if (user.id) {
                  telegramId = user.id.toString()
                  logger.log('✅ telegram_id получен из URL параметров:', telegramId)
                }
              }
            } catch (error) {
              logger.log('⚠️ Ошибка парсинга tgWebAppData из URL:', error)
            }
          }
        }
      }
      
      // ОТЛАДКА: Логируем определение миниапп
      logger.log('🔍 ОТЛАДКА LoginPage:')
      logger.log('- pathname:', pathname)
      logger.log('- isTelegramMode:', isTelegramMode)
      logger.log('- window.Telegram:', !!(window as any).Telegram)
      logger.log('- WebApp:', !!(window as any).Telegram?.WebApp)
      logger.log('- localStorage telegram_id:', localStorage.getItem('telegram_id'))
      logger.log('- telegramId (итоговый):', telegramId)
      
      // ДОПОЛНИТЕЛЬНАЯ ОТЛАДКА: Проверяем Telegram окружение
      const hasTelegramTheme = document.documentElement.hasAttribute('data-telegram-theme')
      const hasTelegramVars = getComputedStyle(document.documentElement).getPropertyValue('--tg-theme-bg-color')
      logger.log('- data-telegram-theme:', hasTelegramTheme)
      logger.log('- --tg-theme-bg-color:', hasTelegramVars)
      
      // КРИТИЧЕСКОЕ ИСПРАВЛЕНИЕ: Если есть признаки Telegram, принудительно включаем режим
      if (!isTelegramMode && (hasTelegramTheme || hasTelegramVars)) {
        isTelegramMode = true
        logger.log('🚨 ПРИНУДИТЕЛЬНО включен Telegram режим по CSS признакам!')
        
        // Попытка получить telegram_id из localStorage или создать фиктивный
        if (!telegramId) {
          telegramId = localStorage.getItem('telegram_id') || '797251551' // Используем известный ID из логов
          logger.log('🔧 Используем telegram_id:', telegramId)
        }
      }
      
      const requestData: any = {
        email: loginData.email,
        password: loginData.password
      }
      
      // Добавляем telegram_id только если он есть (вход через миниапп)
      if (telegramId) {
        requestData.telegram_id = telegramId
        logger.log('✅ Добавляем telegram_id в запрос:', telegramId)
      } else {
        logger.log('❌ telegram_id НЕ найден - это НЕ миниапп')
      }
      
      logger.log('📤 Отправляем запрос:', requestData)
      const response = await http.post('/web_auth/login', requestData)
      
      if (response.data.success && response.data.token) {
        // Определяем режим на основе наличия Telegram WebApp
        const authMode = telegramId ? 'telegram' : 'browser'
        setAuth(response.data.token, response.data.user, authMode)
        setSuccessMessage('Вход выполнен успешно!')
        
        // ИСПРАВЛЕНИЕ: Принудительно переподключаем WebSocket после смены режима
        if (telegramId) {
          logger.log('🔄 Принудительное переподключение WebSocket для Telegram режима')
          logger.log('🔄 Данные для подключения:', { telegramId, token: response.data.token?.substring(0, 20) + '...' })
          
          // 🔥 ИСПРАВЛЕНИЕ: Используем globalWebSocket вместо прямого вызова
          // Даем время авторизации обновиться, затем переподключаем WebSocket
          setTimeout(() => {
            import('../../shared/ws/globalWebSocket').then(({ globalWebSocket }) => {
              logger.log('🔄 Выполняем переподключение WebSocket через 500ms')
              // Используем globalWebSocket который проверит токен
              globalWebSocket.forceReconnect()
            }).catch(error => {
              logger.error('❌ Ошибка переподключения WebSocket:', error)
            })
          }, 500) // Задержка 500ms для обновления состояния
        }
        
        // Даем время токену сохраниться в localStorage
        setTimeout(() => {
          // Перенаправляем с учетом режима
          const target = telegramId ? '/miniapp/orders' : '/orders'
          navigate(target, { replace: true })
        }, 1200)
        
      } else {
        setError(response.data.message || 'Ошибка входа')
      }
      
    } catch (error: any) {
      setError(error.response?.data?.message || 'Произошла ошибка при входе')
    } finally {
      setIsLoading(false)
    }
  }

  const handleRegistrationSuccess = (token: string, user: any) => {
    setAuth(token, user, 'browser')
    setSuccessMessage('Регистрация успешна!')
    
    setTimeout(() => {
      // После logout всегда перенаправляем на главную страницу
      navigate('/orders', { replace: true })
    }, 800)
  }

  const handlePasswordResetRequestSuccess = (message: string) => {
    // Для запроса восстановления - показываем сообщение и возвращаемся к логину
    setSuccessMessage(message)
    setError(null) // Очищаем ошибки
    setTimeout(() => {
      setMode('login')
      setSuccessMessage('') // Очищаем сообщение через некоторое время
    }, 3000) // Показываем сообщение 3 секунды
  }

  const handlePasswordResetSuccess = (message: string) => {
    // Для сброса пароля - не устанавливаем successMessage, чтобы избежать дублирования
    // Сообщение об успехе показывается в самом компоненте PasswordResetConfirmForm
    setError(null) // Очищаем ошибки
  }

  const clearMessages = () => {
    setError(null)
    setSuccessMessage('')
    setFieldErrors({})
  }

  const getFieldError = (field: string) => fieldErrors[field]

  // Стили как в оригинальном веб-фронтенде
  const styles = {
    container: {
      minHeight: '100vh',
      width: '100vw',
      background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'center',
      padding: '0',
      margin: '0',
      position: 'fixed' as const,
      top: '0',
      left: '0',
      right: '0',
      bottom: '0',
      fontFamily: "'Inter', -apple-system, BlinkMacSystemFont, 'Segoe UI', Roboto, 'Helvetica Neue', Arial, sans-serif"
    },
    card: {
      background: 'white',
      borderRadius: '20px',
      boxShadow: '0 20px 25px -5px rgba(0, 0, 0, 0.1), 0 10px 10px -5px rgba(0, 0, 0, 0.04)',
      padding: '2rem',
      width: 'calc(100% - 2rem)',
      maxWidth: '420px',
      margin: '1rem',
      position: 'relative' as const,
      overflow: 'hidden',
      animation: 'slideUp 0.5s ease-out'
    },
    topStripe: {
      position: 'absolute' as const,
      top: 0,
      left: 0,
      right: 0,
      height: '4px',
      background: 'linear-gradient(90deg, #667eea 0%, #764ba2 100%)'
    },
    logo: {
      textAlign: 'center' as const,
      marginBottom: '2rem'
    },
    logoTitle: {
      fontSize: '2rem',
      fontWeight: 700,
      background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
      WebkitBackgroundClip: 'text',
      WebkitTextFillColor: 'transparent',
      backgroundClip: 'text',
      margin: 0
    },
    logoSubtitle: {
      color: '#6b7280',
      marginTop: '0.5rem',
      fontSize: '0.875rem',
      margin: '0.5rem 0 0 0'
    },
    formGroup: {
      marginBottom: '1.5rem'
    },
    formGroupRow: {
      display: 'grid',
      gridTemplateColumns: '1fr 1fr',
      gap: '1rem',
      marginBottom: '1.5rem'
    },
    label: {
      display: 'block',
      color: '#374151',
      fontWeight: 500,
      marginBottom: '0.5rem',
      fontSize: '0.875rem'
    },
    input: {
      width: '100%',
      padding: '0.75rem 1rem',
      border: '2px solid #d1d5db',
      borderRadius: '8px',
      fontSize: '1rem',
      transition: 'all 0.2s ease',
      background: '#f3f4f6',
      boxSizing: 'border-box' as const,
      outline: 'none',
      color: '#374151'
    },
    inputFocus: {
      outline: 'none',
      borderColor: '#667eea',
      background: 'white',
      boxShadow: '0 0 0 3px rgba(102, 126, 234, 0.1)'
    },
    inputError: {
      borderColor: '#ef4444',
      background: '#fef2f2'
    },
    errorMessage: {
      color: '#ef4444',
      fontSize: '0.875rem',
      marginTop: '0.5rem',
      display: 'flex',
      alignItems: 'center',
      gap: '0.25rem'
    },
    successMessage: {
      color: '#10b981',
      fontSize: '0.875rem',
      marginBottom: '1rem',
      padding: '0.75rem',
      background: '#f0fdf4',
      borderRadius: '8px',
      border: '1px solid #bbf7d0',
      display: 'flex',
      alignItems: 'center',
      gap: '0.25rem'
    },
    errorBanner: {
      color: '#ef4444',
      fontSize: '0.875rem',
      marginBottom: '1rem',
      padding: '0.75rem',
      background: '#fef2f2',
      borderRadius: '8px',
      border: '1px solid #fecaca',
      display: 'flex',
      alignItems: 'center',
      gap: '0.25rem'
    },
    button: {
      width: '100%',
      padding: '0.875rem 1.5rem',
      border: 'none',
      borderRadius: '8px',
      fontWeight: 600,
      fontSize: '1rem',
      cursor: 'pointer',
      background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
      color: 'white',
      transition: 'all 0.2s ease',
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'center',
      gap: '0.5rem'
    },
    buttonHover: {
      transform: 'translateY(-1px)',
      boxShadow: '0 10px 25px -5px rgba(102, 126, 234, 0.3)'
    },
    buttonDisabled: {
      opacity: 0.6,
      cursor: 'not-allowed',
      transform: 'none'
    },
    authToggle: {
      textAlign: 'center' as const,
      marginTop: '1.5rem',
      paddingTop: '1.5rem',
      borderTop: '1px solid #e5e7eb'
    },
    authToggleText: {
      color: '#6b7280',
      fontSize: '0.875rem',
      margin: 0
    },
    authToggleButton: {
      color: '#667eea',
      fontWeight: 600,
      background: 'none',
      border: 'none',
      cursor: 'pointer',
      fontSize: '0.875rem',
      textDecoration: 'underline'
    },
    spinner: {
      width: '20px',
      height: '20px',
      border: '2px solid transparent',
      borderTop: '2px solid currentColor',
      borderRadius: '50%',
      animation: 'spin 1s linear infinite'
    }
  }

  // CSS keyframes для анимаций и глобальные стили
  const keyframes = `
    * {
      box-sizing: border-box;
    }
    
    html, body {
      margin: 0;
      padding: 0;
      overflow-x: hidden;
    }
    
    #root {
      margin: 0;
      padding: 0;
    }
    
    @keyframes slideUp {
      from {
        opacity: 0;
        transform: translateY(30px) scale(0.9);
      }
      to {
        opacity: 1;
        transform: translateY(0) scale(1);
      }
    }
    
    @keyframes spin {
      to { transform: rotate(360deg); }
    }
    
    @keyframes fadeIn {
      from { opacity: 0; transform: translateY(10px); }
      to { opacity: 1; transform: translateY(0); }
    }
  `

  return (
    <>
      <style>{keyframes}</style>
      <style>{`
        input::placeholder {
          color: #9ca3af !important;
          opacity: 1;
        }
      `}</style>
      <AnimatedBackground>
        <div style={{
          ...styles.card,
          background: 'white',
          borderRadius: '24px',
          boxShadow: '0 25px 50px -12px rgba(0, 0, 0, 0.25)',
          padding: '2rem',
          width: 'calc(100% - 2rem)',
          maxWidth: '420px',
          margin: '1rem',
          position: 'relative' as const,
          overflow: 'hidden'
        }}>
          {/* Верхняя полоска */}
          <div style={styles.topStripe} />

          {/* Логотип - показываем только в режиме входа */}
          {mode === 'login' && (
            <div style={styles.logo}>
              <h1 style={styles.logoTitle}>Advance</h1>
              <p style={styles.logoSubtitle}>Портал поставщиков</p>
            </div>
          )}

          {/* Сообщения */}
          {error && !shouldHideRegistrationError(error) && (
            <div style={styles.errorBanner}>
              <span>❌</span> {error}
            </div>
          )}

          {successMessage && (
            <div style={styles.successMessage}>
              <span>✅</span> {successMessage}
            </div>
          )}

          {/* Информационное сообщение для Telegram - только в режиме логина */}
          {(window as any).Telegram?.WebApp && mode === 'login' && (
            <div style={{
              backgroundColor: '#e3f2fd',
              border: '1px solid #2196f3',
              borderRadius: '8px',
              padding: '12px',
              marginBottom: '20px',
              fontSize: '14px',
              color: '#1976d2'
            }}>
              <strong>📱 Telegram аккаунт</strong><br />
              После входа ваш Telegram будет автоматически привязан к аккаунту.
            </div>
          )}

          {/* Контент в зависимости от режима */}
          {mode === 'login' ? (
            <>
              <form onSubmit={handleLogin}>
                <div style={styles.formGroup}>
                  <label style={styles.label}>Email</label>
                  <input
                    type="email"
                    value={loginData.email}
                    onChange={(e) => {
                      setLoginData(prev => ({ ...prev, email: e.target.value }))
                      clearMessages()
                    }}
                    onFocus={(e) => Object.assign(e.target.style, styles.inputFocus)}
                    onBlur={(e) => {
                      Object.assign(e.target.style, {
                        ...styles.input,
                        ...(getFieldError('email') ? styles.inputError : {})
                      })
                    }}
                    style={{
                      ...styles.input,
                      ...(getFieldError('email') ? styles.inputError : {})
                    }}
                    required
                  />
                  {getFieldError('email') && (
                    <div style={styles.errorMessage}>
                      <span>⚠️</span> {getFieldError('email')}
                    </div>
                  )}
                </div>

                <div style={styles.formGroup}>
                  <label style={styles.label}>Пароль</label>
                  <input
                    type="password"
                    value={loginData.password}
                    onChange={(e) => {
                      setLoginData(prev => ({ ...prev, password: e.target.value }))
                      clearMessages()
                    }}
                    onFocus={(e) => Object.assign(e.target.style, styles.inputFocus)}
                    onBlur={(e) => {
                      Object.assign(e.target.style, {
                        ...styles.input,
                        ...(getFieldError('password') ? styles.inputError : {})
                      })
                    }}
                    style={{
                      ...styles.input,
                      ...(getFieldError('password') ? styles.inputError : {})
                    }}
                    required
                  />
                  {getFieldError('password') && (
                    <div style={styles.errorMessage}>
                      <span>⚠️</span> {getFieldError('password')}
                    </div>
                  )}
                </div>

                <div style={{textAlign: 'right', marginBottom: '1rem'}}>
                  <button
                    type="button"
                    onClick={() => setMode('password_reset')}
                    style={{
                      background: 'none',
                      border: 'none',
                      color: '#667eea',
                      cursor: 'pointer',
                      fontSize: '0.875rem',
                      textDecoration: 'underline'
                    }}
                  >
                    Забыли пароль?
                  </button>
                </div>

                <button
                  type="submit"
                  disabled={isLoading}
                  style={{
                    ...styles.button,
                    ...(isLoading ? styles.buttonDisabled : {})
                  }}
                  onMouseOver={(e) => !isLoading && Object.assign(e.currentTarget.style, styles.buttonHover)}
                  onMouseOut={(e) => !isLoading && Object.assign(e.currentTarget.style, { transform: 'none', boxShadow: 'none' })}
                >
                  {isLoading ? (
                    <>
                      <div style={styles.spinner} />
                      Входим...
                    </>
                  ) : (
                    <>
                      <span>🔐</span> Войти
                    </>
                  )}
                </button>
              </form>
            </>
          ) : mode === 'register' ? (
            <RegistrationForm
              onSuccess={handleRegistrationSuccess}
              onError={setError}
              onSwitchToLogin={() => {
                setMode('login')
                clearMessages()
              }}
              onSwitchToPasswordReset={() => {
                setMode('password_reset')
                clearMessages()
              }}
            />
          ) : mode === 'password_reset' ? (
            <PasswordResetRequestForm
              onBack={() => setMode('login')}
              onSuccess={handlePasswordResetRequestSuccess}
              onError={setError} // Передаем ошибки в LoginPage
            />
          ) : mode === 'password_reset_confirm' && resetToken ? (
            <PasswordResetConfirmForm
              token={resetToken}
              onBack={() => setMode('login')}
              onSuccess={handlePasswordResetSuccess}
              onError={() => {}} // Не передаем ошибки в LoginPage
            />
          ) : null}

          {/* Переключатель форм - только для режима входа */}
          {mode === 'login' && (
            <div style={styles.authToggle}>
              <p style={styles.authToggleText}>
                Нет аккаунта?
                {' '}
                <button
                  type="button"
                  onClick={() => {
                    setMode('register')
                    clearMessages()
                  }}
                  style={styles.authToggleButton}
                  onMouseOver={(e) => e.currentTarget.style.textDecoration = 'underline'}
                  onMouseOut={(e) => e.currentTarget.style.textDecoration = 'none'}
                >
                  Зарегистрироваться
                </button>
              </p>
            </div>
          )}
        </div>
      </AnimatedBackground>
    </>
  )
}